/**
 * Geometry, z-order and persistence for the viewer's movable windows
 * (spec §12.1 / §12.2).
 *
 * All of it is pure and side-effect free — apart from the one `safeStorage`
 * helper at the bottom — so the awkward parts are unit-testable without a DOM:
 * the clamp that keeps a window reachable, the offset-from-anchor round trip
 * that makes a saved position survive a viewport change, and the fixed z band
 * the windows rotate inside.
 */

export type WindowAnchor = { top?: number; bottom?: number; left?: number; right?: number };

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Size {
  w: number;
  h: number;
}

/** How much of a window must stay on screen HORIZONTALLY, in px. Small enough
 *  to let a user park a panel almost entirely off the left or right edge,
 *  large enough that its sliver can always be grabbed back. */
export const WINDOW_MIN_VISIBLE_PX = 24;

/**
 * The height of a window's drag handle, in px — also its touch target
 * (spec §12.1's 44 px). `FloatingWindow` applies this to the handle it hands
 * its consumer, and `clampToBounds` uses it as the vertical rule: the handle
 * row itself must never leave the region (ruling D41). 24 px of a *window*
 * showing above the top edge is useless if the 24 px is the window's bottom
 * border and the grip is 300 px off-screen — the only recovery would be
 * "Reset layout", which resets all three windows.
 */
export const GRIP_HEIGHT_PX = 44;

/** The margin the §12.2 defaults leave against the edges of the canvas region. */
export const WINDOW_MARGIN_PX = 8;

// --------------------------------------------------------------------- maths

export function anchorToRect(anchor: WindowAnchor, size: Size, bounds: Size): Rect {
  const x = anchor.left !== undefined ? anchor.left : anchor.right !== undefined ? bounds.w - size.w - anchor.right : 0;
  const y = anchor.top !== undefined ? anchor.top : anchor.bottom !== undefined ? bounds.h - size.h - anchor.bottom : 0;
  return { x, y, w: size.w, h: size.h };
}

/**
 * Keep the window reachable inside `bounds` (ruling D41):
 *
 * - horizontally, at least `minVisible` px of the rect stays inside on the
 *   left and right (spec §12.1);
 * - vertically, `0 ≤ y ≤ bounds.h − gripHeight`, so the grip row is always on
 *   screen and the window can be dragged back by hand.
 */
export function clampToBounds(
  rect: Rect,
  bounds: Size,
  minVisible = WINDOW_MIN_VISIBLE_PX,
  gripHeight = GRIP_HEIGHT_PX,
): { x: number; y: number } {
  return {
    x: Math.max(minVisible - rect.w, Math.min(bounds.w - minVisible, rect.x)),
    y: Math.max(0, Math.min(bounds.h - gripHeight, rect.y)),
  };
}

/**
 * A dragged position, stored as a delta from where the window's ANCHOR puts
 * it. Storing a raw `x` would send a right-anchored panel off the edge the
 * first time the user opened the app on a narrower screen; storing the delta
 * keeps "12 px left of where it belongs" meaning the same thing at any width.
 */
export function offsetFromAnchor(
  rect: Rect,
  anchor: WindowAnchor,
  size: Size,
  bounds: Size,
): { dx: number; dy: number } {
  const base = anchorToRect(anchor, size, bounds);
  return { dx: rect.x - base.x, dy: rect.y - base.y };
}

export function rectFromOffset(
  offset: { dx: number; dy: number },
  anchor: WindowAnchor,
  size: Size,
  bounds: Size,
): Rect {
  const base = anchorToRect(anchor, size, bounds);
  return { x: base.x + offset.dx, y: base.y + offset.dy, w: size.w, h: size.h };
}

export const rectsIntersect = (a: Rect, b: Rect): boolean =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

// ------------------------------------------------------------------ defaults

export type ViewerWindowId = 'minimap' | 'walkToolbar' | 'analysisColumn';

/**
 * Spec §12.2's collision-free defaults: minimap 320 px top-left, the walk
 * toolbar starting at `left: 340`, the analysis column 384 px top-right.
 *
 * `WINDOW_DEFAULT_SIZES` are the *slots* those defaults reserve, and they are
 * what the non-overlap test checks. The walk toolbar renders at its own
 * intrinsic width and can exceed its 540 px slot on a narrow viewport — it is
 * not clipped. When it does, the user drags it, which is what this feature is
 * for; a hard width would break a toolbar owned by another workstream.
 */
export const WINDOW_DEFAULT_ANCHORS: Record<ViewerWindowId, WindowAnchor> = {
  minimap: { top: WINDOW_MARGIN_PX, left: WINDOW_MARGIN_PX },
  walkToolbar: { top: WINDOW_MARGIN_PX, left: 340 },
  analysisColumn: { top: WINDOW_MARGIN_PX, right: WINDOW_MARGIN_PX },
};

export const WINDOW_DEFAULT_SIZES: Record<ViewerWindowId, Size> = {
  minimap: { w: 320, h: 320 },
  walkToolbar: { w: 540, h: 56 },
  analysisColumn: { w: 384, h: 640 },
};

/** The analysis column's tallest useful slot; it scrolls inside whatever it gets. */
export const ANALYSIS_COLUMN_MAX_H = 640;

/**
 * The default slots for a given canvas region.
 *
 * Only the analysis column depends on the region: with the station dock open
 * at its 280 px default the region is 1280 × 440, and a fixed 640 px column
 * would hang 200 px below the canvas. Its height is therefore
 * `min(640, regionHeight − 2 × margin)` and the column scrolls inside it.
 */
export function defaultWindowSizes(bounds: Size): Record<ViewerWindowId, Size> {
  return {
    minimap: WINDOW_DEFAULT_SIZES.minimap,
    walkToolbar: WINDOW_DEFAULT_SIZES.walkToolbar,
    analysisColumn: {
      w: WINDOW_DEFAULT_SIZES.analysisColumn.w,
      h: Math.max(0, Math.min(ANALYSIS_COLUMN_MAX_H, bounds.h - 2 * WINDOW_MARGIN_PX)),
    },
  };
}

export function defaultWindowRects(bounds: Size): Record<ViewerWindowId, Rect> {
  const sizes = defaultWindowSizes(bounds);
  const ids = Object.keys(WINDOW_DEFAULT_ANCHORS) as ViewerWindowId[];
  return Object.fromEntries(
    ids.map((id) => [id, anchorToRect(WINDOW_DEFAULT_ANCHORS[id], sizes[id], bounds)]),
  ) as Record<ViewerWindowId, Rect>;
}

// --------------------------------------------------------------------- z band

/**
 * The z band the windows live in (controller ruling D27 — deliberately NOT an
 * unbounded "top z" counter, which would eventually climb over the modals and
 * toasts it must always stay under).
 *
 * There are exactly `WINDOW_COUNT` windows, so `WINDOW_COUNT` z slots are
 * enough to express any stacking order: `[WINDOW_BASE_Z + 1 … WINDOW_MAX_Z]`,
 * with `WINDOW_BASE_Z` itself for a window nobody has touched yet.
 *
 * The band starts at **21**, one above `FloatingToolbar`'s `z-20` (ruling
 * D48): every other top-centre tool toolbar — Add Point / Line / Polygon /
 * Surface, Stockpile, the elevation-profile hint — renders at `z-20`, and at
 * equal z the later DOM node wins, which would put them over the walk toolbar
 * window. A covered window cannot be raised by clicking it, because the thing
 * covering it swallows the pointerdown. The band ends at 24; dialogs (`z-50`)
 * and toasts sit far above it.
 */
export const WINDOW_BASE_Z = 21;
export const WINDOW_COUNT = 3;
export const WINDOW_MAX_Z = WINDOW_BASE_Z + WINDOW_COUNT;

/**
 * The z a window renders at, given the stacking order (back → front, at most
 * `WINDOW_COUNT` entries). The front-most id always gets `WINDOW_MAX_Z`, so
 * bringing a window to the front pushes the others down a slot rather than
 * climbing.
 */
export const windowZIndex = (stack: readonly string[], id: string): number => {
  const i = stack.indexOf(id);
  return i < 0 ? WINDOW_BASE_Z : WINDOW_MAX_Z - (stack.length - 1 - i);
};

// ------------------------------------------------------------------- storage

export const WINDOW_STORAGE_PREFIX = '@actegon.gis.viewer.window.';
export const windowStorageKey = (id: string) => `${WINDOW_STORAGE_PREFIX}${id}`;

/**
 * The one place in this feature that touches `localStorage`, and the only
 * place with empty `catch` blocks (ruling D6). Storage throws outright in
 * private mode and when site data is blocked, and a window that cannot
 * remember where it was is not an error worth surfacing to anyone — the
 * window still moves, it just starts at its anchor next time.
 */
export const safeStorage = {
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      // Storage unavailable — treat it as "nothing stored".
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage unavailable or full — the position simply is not remembered.
    }
  },
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage unavailable — there was nothing to remove.
    }
  },
  /** Every key currently stored. Needed to clear the whole namespace. */
  keys(): string[] {
    try {
      const out: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k !== null) out.push(k);
      }
      return out;
    } catch {
      // Storage unavailable — there is nothing to enumerate.
      return [];
    }
  },
};

/** The stored offset for a window, or `null` when there is none worth trusting. */
export function readWindowOffset(id: string): { dx: number; dy: number } | null {
  const raw = safeStorage.get(windowStorageKey(id));
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // A hand-edited or truncated entry: fall back to the anchor.
    return null;
  }
  const v = parsed as { dx?: unknown; dy?: unknown } | null;
  if (!v || typeof v.dx !== 'number' || typeof v.dy !== 'number') return null;
  return Number.isFinite(v.dx) && Number.isFinite(v.dy) ? { dx: v.dx, dy: v.dy } : null;
}

export function writeWindowOffset(id: string, offset: { dx: number; dy: number }): void {
  safeStorage.set(windowStorageKey(id), JSON.stringify(offset));
}
